import { TimeObject } from "../../modules/alarms/types";

export const cpfMask = (value: string) => {
  return value
    .replace(/\D/g, "")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})/, "$1-$2")
    .replace(/(-\d{2})\d+?$/, "$1");
};

export const emailRegex = /^[a-z0-9.]+@[a-z0-9]+\.[a-z]/;

export const phoneRegex = /^\+\d{2}\s\(\d{2}\)\s\d{5}-\d{4}$/;

export const cnpjRegex = /^\d{14}$/;
/**
 * https://www.geradorcpf.com/javascript-validar-cpf.htm
 * @returns
 */
export function validateCPF(cpfIn: unknown): boolean {
  if (typeof cpfIn !== "string") {
    return false;
  }

  const cpf = `${cpfIn}`.replace(/[^\d]+/g, "");
  if (cpf === "") return true; //SE FOR VAZIO NÃO TEM O QUE VALIDAR
  // Elimina CPFs invalidos conhecidos
  if (
    cpf.length !== 11 ||
    cpf === "00000000000" ||
    cpf === "11111111111" ||
    cpf === "22222222222" ||
    cpf === "33333333333" ||
    cpf === "44444444444" ||
    cpf === "55555555555" ||
    cpf === "66666666666" ||
    cpf === "77777777777" ||
    cpf === "88888888888" ||
    cpf === "99999999999"
  )
    return false;
  // Valida 1o digito
  let add = 0;
  for (let i = 0; i < 9; i++) add += parseInt(cpf.charAt(i)) * (10 - i);
  let rev = 11 - (add % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(cpf.charAt(9))) return false;
  // Valida 2o digito
  add = 0;
  for (let i = 0; i < 10; i++) add += parseInt(cpf.charAt(i)) * (11 - i);
  rev = 11 - (add % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(cpf.charAt(10))) return false;
  return true;
}

export function formatDate(date: Date | string, isNotBetweenBars?: boolean) {
  const data = new Date(date);
  const dia = data.getDate();
  const mes = data.getMonth() + 1;
  const ano = data.getFullYear();

  const diaFormatado = dia < 10 ? `0${dia}` : dia;
  const mesFormatado = mes < 10 ? `0${mes}` : mes;

  return isNotBetweenBars
    ? `${mesFormatado}-${diaFormatado}-${ano}`
    : `${diaFormatado}/${mesFormatado}/${ano}`;
}

export function formatHour(date: Date | string, hasSeconds?: boolean) {
  const data = new Date(date);
  const hora = data.getHours();
  const minuto = data.getMinutes();
  const segundo = data.getSeconds();

  const horaFormatada = hora < 10 ? `0${hora}` : hora;
  const minutoFormatado = minuto < 10 ? `0${minuto}` : minuto;
  const segundoFormatado = segundo < 10 ? `0${segundo}` : segundo;
  if (hasSeconds) {
    return `${horaFormatada}:${minutoFormatado}:${segundoFormatado}`;
  }
  return `${horaFormatada}:${minutoFormatado}`;
}

export function getChartHourLabel(dateTime?: string): string {
  if (!dateTime) {
    return "";
  }

  const trimmed = dateTime.trim();
  if (!trimmed) {
    return "";
  }

  const hasExplicitTimezone = /(?:[Zz]|[+-]\d{2}:?\d{2}(?::\d{2})?)$/.test(
    trimmed,
  );
  const isoTimeMatch = trimmed.match(/T(\d{2}:\d{2})/);

  const parsedDate =
    !hasExplicitTimezone && isoTimeMatch
      ? new Date(`${trimmed}Z`)
      : new Date(trimmed);

  if (!Number.isNaN(parsedDate.getTime())) {
    const parts = new Intl.DateTimeFormat("pt-BR", {
      timeZone: "America/Sao_Paulo",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(parsedDate);

    const hour = parts.find((part) => part.type === "hour")?.value ?? "00";
    const minute = parts.find((part) => part.type === "minute")?.value ?? "00";

    return `${hour}:${minute}`;
  }

  if (isoTimeMatch?.[1]) {
    return isoTimeMatch[1];
  }

  return formatHour(trimmed);
}

export function formatDateAndHour(date: Date | string, hasSeconds?: boolean) {
  return `${formatDate(date)} - ${formatHour(date, hasSeconds)}`;
}

export function formatDataHoraCaptura(date: string): string {
  if (date === "---") return "";
  return formatDateAndHour(date);
}

export function parseTimeSpan(activeTime: TimeObject | null): string {
  const [horas, minutos, segundos] = (activeTime?.hours || "").split(":").map(Number);

  const horasFormatada = horas > 0 ? `${horas}hr` : ""
  const minutosFormatado = minutos > 0 ? `${minutos}min` : ""
  const segundosFormatado = segundos > 0 ? `${segundos}seg` : ""

  const tempoFormatado = `${horasFormatada} ${minutosFormatado} ${segundosFormatado}`

  if (activeTime?.days && activeTime?.days !== 0) {
    if (activeTime?.days === 1) {
      return `${activeTime?.days} dia e ${tempoFormatado}`;
    } else {
      return `${activeTime?.days} dias e ${tempoFormatado}`;
    }
  } else {
    return `${tempoFormatado}`;
  }
}

export function getTextAfterLastDot(inputString: string) {
  const match = inputString.match(/[^.]*$/);
  if (match) {
    return match[0];
  }
  return "";
}

export function numberPtBrPercent01(
  number: number,
  maximumFractionDigits?: number,
  minimumFractionDigits?: number
): string {
  return number.toLocaleString("pt-BR", {
    style: "percent",
    minimumFractionDigits,
    maximumFractionDigits,
  });
}

export function numberPtBrPercent0100(
  number: number,
  maximumFractionDigits?: number,
  minimumFractionDigits?: number
): string {
  return numberPtBrPercent01(
    number / 100,
    maximumFractionDigits,
    minimumFractionDigits
  );
}

export function numberPtBr(
  number: number,
  maximumFractionDigits?: number,
  minimumFractionDigits?: number
): string {
  return number.toLocaleString("pt-BR", {
    minimumFractionDigits,
    maximumFractionDigits,
  });
}

export function numberPtBrCurrency(value: number | string): string {
  const valorFormatado = Number(value).toFixed(2);

  const [parteInteira, parteDecimal] = valorFormatado.split(".");

  const parteInteiraComSeparador = parteInteira.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    "."
  );

  return `R$ ${parteInteiraComSeparador},${parteDecimal}`;
}

export function formatDateRequest(date: Date | string) {
  const data = new Date(date);
  const dia = data.getDate();
  const mes = data.getMonth() + 1;
  const ano = data.getFullYear();

  const diaFormatado = dia < 10 ? `0${dia}` : dia;
  const mesFormatado = mes < 10 ? `0${mes}` : mes;

  return `${ano}-${mesFormatado}-${diaFormatado}`;
}

export function formatTimeSpanToString(time: number, removeSeconds?: boolean) {
  const timeWithoutSeconds = time.toString().split(".")[0];
  const timeHour = Math.floor(Number(timeWithoutSeconds) / 60);
  const timeMinutes =
    Number(timeHour) > 0
      ? Number(timeWithoutSeconds) % 60
      : Number(time.toString().split(".")[0]) ?? 0;
  const timeSeconds = String(time).split(".")[1] ?? 0;

  const hora = timeHour < 10 ? `0${timeHour}` : timeHour;
  const minuto = timeMinutes < 10 ? `0${timeMinutes}` : timeMinutes;
  const getSeconds = () => {
    if (Number(timeSeconds) == 0) return "00";
    if (timeSeconds.length === 1) return `${timeSeconds}0`;
    return timeSeconds;
  };

  if (time > 60) {
    return removeSeconds
      ? `${hora}:${minuto} h`
      : `${hora}:${minuto}:${getSeconds()} h`;
  }
  if (Number(minuto) > 0) {
    return removeSeconds ? `${minuto} min` : `${minuto}:${getSeconds()} min`;
  }
  return `${getSeconds()} seg`;
}

export function getDayMonthAndYearFromDate(date: Date | string): {
  dia?: string;
  mes: string;
  ano: string;
} {
  const data = String(date).split("-");
  const dia = data[2];
  const mes = data[1];
  const ano = data[0];

  return {
    dia: dia,
    mes: mes,
    ano: ano,
  };
}

export function toFixedNoRound(value: number, decimals: number) {
  const factor = Math.pow(10, decimals);
  return Math.floor(value * factor) / factor;
}

export function isNumberNullOrNegative(
  value: number | null | undefined | string
): boolean {
  const stringToNumber = Number(value);
  if (stringToNumber == null) return true;
  if (stringToNumber < 0) return true;
  if (stringToNumber == -0) return true;
  return false;
}

export function convertTimeToMinutes(timeStr: string): number {
  const [hours, minutes, seconds] = timeStr.split(':').map(Number);
  const currentSeconds = seconds ?? 0;
  return (
    (hours ?? 0) * 60 +
    (minutes ?? 0) +
    (currentSeconds >= 60 ? Number((currentSeconds / 60).toFixed(0)) : 0)
  );
}

export function parseTimeSpanToSeconds(timespan: string): number {
  let days = 0, hours = 0, minutes = 0, seconds = 0;

  // Se tiver ponto, significa que há dias
  const [dayPart, timePart] = timespan.includes(".")
    ? timespan.split(".")
    : [null, timespan];

  if (dayPart) {
    days = Number(dayPart);
  }

  const timeSegments = timePart.split(":").map(Number);

  if (timeSegments.length === 3) {
    hours = timeSegments[0] ?? 0;
    minutes = timeSegments[1] ?? 0;
    seconds = timeSegments[2] ?? 0;
  }

  return days * 86400 + hours * 3600 + minutes * 60 + seconds;
}

export function secondsToTimeSpanString(totalSeconds: number): string {
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n: number) => n.toString().padStart(2, "0");

  const time = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  return days > 0 ? `${days}.${time}` : time;
}